import { toastError } from "../../toasts";
import { BaBase } from "./ba-base.js";
import {  BonusAddFile } from "./bonus-add-ai";
export interface DragDropEvents {
    /** Called when files are dropped */
    onFileDrop?: (files: File[]) => void;
    /** Called on drag enter */
    onDragEnter?: (event: DragEvent) => void;
    /** Called on drag over */
    onDragOver?: (event: DragEvent) => void;
    /** Called on drag leave */
    onDragLeave?: (event: DragEvent) => void;
    /** Called on validation error */
    onError?: (error: string, files?: File[]) => void;
}
export class BaFile extends BaBase {
    static override webComponentName: string = "ba-file";
    protected override _templateId: string = "ba-file";
    fileInput!: HTMLInputElement;
    labelInput!: HTMLLabelElement;
    private events: DragDropEvents;
    private dragCounter: number = 0;
    private isDragActive: boolean = false;
    dragActiveClass: string = 'drag-active';
    dragOverClass: string = 'drag-over';
    
    public get accept(): string {
        return this.attributeStringOption('accept', 'application/pdf');
    }
    public get maxSize(): number {
        return this.attributeNumberOption('max-size', 10 * 1024 * 1024); // default 10MB
    }

    private isValidFileType(file: File): boolean {
        const accept = this.accept;
        if (!accept) {
            return true; // se non c'è restrizione, accetta tutto
        }
        const mimeTypes = accept.split(',').map(type => type.trim());
        return mimeTypes.includes(file.type) || mimeTypes.some(type => type.endsWith('/*') && file.type.startsWith(type.replace('/*', '/')));
    }
    private isValidFileSize(file: File): boolean {
        return file.size <= this.maxSize;
    }
    private invalidFileMessage(file: File): string | null {
        if (!this.isValidFileType(file)) {
            return `Tipo di file non valido. Sono accettati solo i tipi: ${this.accept}`;
        }
        if (!this.isValidFileSize(file)) {
            return `Il file supera la dimensione massima consentita di ${this.maxSize / (1024 * 1024)} MB.`;
        }
        return null;
    }
    constructor() {
        super();
        
        
        this.events = {
            onFileDrop: (files: File[]) => {
                if (files.length > 0) {
                    this.filePicked(files[0]);
                }
            },
            onError: (error: string, files?: File[]) => {
                toastError(`Errore nel caricamento del file: ${error}`);
            }
        }
    }
    protected fileToBase64(file: File): Promise<string> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
                const result = reader.result as string;
                
                // Verifica che il risultato sia nel formato corretto
                if (!result || !result.includes(',')) {
                    reject(new Error('Formato dati non valido'));
                    return;
                }
                
                const base64 = result.split(',')[1]; // Rimuove il prefisso data:...;base64,
                
                // Verifica che ci sia effettivamente contenuto base64
                if (!base64) {
                    reject(new Error('Impossibile estrarre i dati base64'));
                    return;
                }
                
                resolve(base64);
            };
            reader.onerror = (error) => {
                reject(new Error(`Errore nella lettura del file: ${error}`));
            };
            reader.readAsDataURL(file);
        });
    }
    // quando il file è selezionato
    protected async filePicked(file: File): Promise<void> {
        const invalidMessage = this.invalidFileMessage(file);
        if (invalidMessage) {
            toastError(invalidMessage);
            return;
        }
        try {
            const base64 = await this.fileToBase64(file);
            const baFile: BonusAddFile = Object.assign(file, { base64 });
            
            if (this.BaParent) {
                this.BaParent.currentFile = baFile;
            }
        } catch (error) {
            console.log(error);
            toastError('Errore nella lettura del file.');
            return;
        }
    }
    protected buildRenderingData(): Record<string, any> {
        const baseData = super.buildRenderingData();
        return {
            accept: this.accept,
            ...baseData
        };
    }
    private preventDefaults(e: Event): void {
        e.preventDefault();
        e.stopPropagation();
    }
    protected onConnected(): void {
        super.onConnected();
        this.preventBodyDrop();
    }

    private preventBodyDrop(): void {
        // Prevent default drag behaviors on body
        ['dragenter', 'dragover', 'dragleave', 'drop'].forEach(eventName => {
            document.body.addEventListener(eventName, (e: Event) => {
                e.preventDefault();
                e.stopPropagation();
            }, false);
        });
    }
    private setupDragDropEventListeners(): void {
        // Prevent default browser behavior for drag events
        ['dragenter', 'dragover', 'dragleave', 'drop'].forEach(eventName => {
            this.labelInput.addEventListener(eventName, this.preventDefaults.bind(this), false);
        });

        // Handle drag enter/over for visual feedback
        ['dragenter', 'dragover'].forEach(eventName => {
            this.labelInput.addEventListener(eventName, this.handleDragEnterOver.bind(this), false);
        });

        // Handle drag leave/drop to remove visual feedback
        ['dragleave', 'drop'].forEach(eventName => {
            this.labelInput.addEventListener(eventName, this.handleDragLeaveEnd.bind(this), false);
        });

        // Handle the actual file drop
        this.labelInput.addEventListener('drop', this.handleFileDrop.bind(this), false);
    }
    private handleDragEnterOver(e: DragEvent): void {
        this.dragCounter++;

        if (!this.isDragActive) {
            this.isDragActive = true;
            this.classList.add(this.dragActiveClass);
        }

        // Check if dragged items contain files
        if (this.containsFiles(e)) {
            this.classList.add(this.dragOverClass);
            this.events.onDragOver?.(e);
        }

        this.events.onDragEnter?.(e);
    }

    private handleDragLeaveEnd(e: DragEvent): void {
        // Per dragleave, controlla se stiamo davvero uscendo dall'elemento
        if (e.type === 'dragleave') {
            const rect = this.labelInput.getBoundingClientRect();
            const isOutside = e.clientX < rect.left || e.clientX > rect.right || 
                             e.clientY < rect.top || e.clientY > rect.bottom;
            
            if (!isOutside) {
                return; // Non siamo davvero usciti dall'area
            }
        }
        
        // Per drop o quando usciamo davvero dall'area
        this.dragCounter = 0; // Reset completo
        this.isDragActive = false;
        this.classList.remove(this.dragActiveClass);
        this.classList.remove(this.dragOverClass);
        
        if (e.type === 'dragleave') {
            this.events.onDragLeave?.(e);
        }
    }

    private containsFiles(e: DragEvent): boolean {
        if (!e.dataTransfer?.types) return false;

        return Array.from(e.dataTransfer.types).includes('Files');
    }
    private extractFiles(e: DragEvent): File[] {
        const files: File[] = [];

        if (e.dataTransfer?.items) {
            // Use DataTransferItemList interface
            for (let i = 0; i < e.dataTransfer.items.length; i++) {
                const item = e.dataTransfer.items[i];
                if (item.kind === 'file') {
                    const file = item.getAsFile();
                    if (file) {
                        files.push(file);
                    }
                }
            }
            e.dataTransfer.items.clear();
        } else if (e.dataTransfer?.files) {
            // Fallback to FileList
            files.push(...Array.from(e.dataTransfer.files));
            e.dataTransfer.clearData();
        }

        return files;
    }
    private handleFileDrop(e: DragEvent): void {
        this.dragCounter = 0;
        this.isDragActive = false;
        this.classList.remove(this.dragActiveClass);
        this.classList.remove(this.dragOverClass);

        const files = this.extractFiles(e);

        if (files.length === 0) {
            this.events.onError?.('No files detected in drop event');
            return;
        }
        const firstFile = files[0];
        // gli errori di validazione vengono gestiti in filePicked
        this.events.onFileDrop?.([firstFile]);
    }
    protected afterRender(): void {
        this.fileInput = this.querySelector(`#file-input-${this.componentId}`) as HTMLInputElement;
        this.labelInput = this.querySelector(`.file-label`) as HTMLLabelElement;
        this.fileInput?.addEventListener('change', (event: Event) => {
            const input = event.target as HTMLInputElement;
            if (input.files && input.files.length > 0) {
                const file = input.files[0];
               
                this.filePicked(file);
                // Resetta il valore per permettere di selezionare lo stesso file di nuovo
                input.value = '';
            }
        });
        this.setupDragDropEventListeners();
    }
}